import React, { useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";
import { DataContext } from "../../../context/DataContext";
import { simpleMessage } from "../../../helpers/Helpers";
import { ConfirmDialog, useAviso } from "../../../components/base";
import { formatearFecha } from "../../../helpers/formato";
import {
  ERROR_OTRA_SESION,
  encabezadoTabla,
  llevarAlPrimerError,
  normalizar,
  ocultoVisual,
} from "../../clients/comunes";

// Piezas que comparten la lista, el alta y el detalle de empleados (RRHH II).

export {
  ERROR_OTRA_SESION,
  encabezadoTabla,
  llevarAlPrimerError,
  normalizar,
  ocultoVisual,
};

// Respeta "reducir movimiento": el esqueleto queda quieto.
const sinAnimacion = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

// Columnas de montos: a la derecha y con cifras tabulares.
export const celdaMonto = {
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

export const celdaFecha = {
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

// Una fecha que ya no es la de hoy (el valor inicial de los diálogos de alta): cuenta como algo escrito.
export const fechaCambiada = (fecha) => !moment(fecha).isSame(moment(), "day");

// Lo que se puede escribir en un campo de monto: dígitos y un solo punto (igual que antes).
export const esMontoEscribible = (valor) =>
  /^\d*\.?\d*$/.test(String(valor)) || valor === "";

/**
 * Antigüedad de un empleado, con la misma cuenta que hace el sistema en la pestaña de Vacaciones
 * (años de 360 días y meses de 30). Se calcula con la fecha de ingreso que ya está cargada.
 * @returns {string|null} Ej. "2 años, 3 meses"; "12 días" si lleva menos de un mes; null si la fecha no sirve.
 */
export const antiguedadDesde = (fechaIngreso, ahora = new Date()) => {
  // `moment(undefined)` es "ahora": sin fecha no se calcula nada.
  if (fechaIngreso === undefined || fechaIngreso === null || fechaIngreso === "") return null;
  const inicio = moment(fechaIngreso);
  if (!inicio.isValid() || inicio.year() <= 1) return null;
  const dias = Math.abs(moment(ahora).diff(inicio, "days", true));
  const factor = dias / 360;
  const anios = Math.trunc(factor);
  const factorMes = (factor - anios) * 12;
  const meses = Math.trunc(factorMes);
  const resto = Math.trunc((factorMes - meses) * 30);

  return textoAntiguedad(anios, meses, resto);
};

/** Mismo formato para toda la antigüedad: "2 años, 3 meses"; solo días si lleva menos de un mes. */
export const textoAntiguedad = (anios, meses, dias) => {
  const partes = [];
  if (anios > 0) partes.push(`${anios} ${anios === 1 ? "año" : "años"}`);
  if (meses > 0) partes.push(`${meses} ${meses === 1 ? "mes" : "meses"}`);
  if (partes.length === 0) return `${dias} ${dias === 1 ? "día" : "días"}`;
  return partes.join(", ");
};

/**
 * Presenta la antigüedad que manda el servidor ("5 DÍAS, 0 MESES, 1 AÑOS") con el mismo formato del
 * resumen del empleado. No recalcula nada: solo reordena el texto. Si no reconoce el texto lo deja tal cual.
 */
export const formatearAntiguedadServidor = (texto) => {
  const m = /^\s*(\d+)\s+D[ÍI]AS,\s*(\d+)\s+MESES,\s*(\d+)\s+A[ÑN]OS\s*$/i.exec(
    String(texto ?? "")
  );
  if (!m) return texto;
  return textoAntiguedad(Number(m[3]), Number(m[2]), Number(m[1]));
};

/**
 * Carga local de una pantalla o pestaña: esqueleto mientras llega, error con "Reintentar" y sin
 * dejar el overlay global encendido. `pedir` devuelve lo que devuelve la capa API
 * ({ statusResponse, data, error }). `cargar()` resuelve con ese resultado, o con null si falló
 * (o si una carga más nueva ya lo reemplazó).
 */
export const useCargaLocal = (pedir) => {
  const { setIsUnautorized } = useContext(DataContext);
  const [estado, setEstado] = useState({ cargando: true, error: false });
  const actual = useRef(0);
  const pedirActual = useRef(pedir);
  pedirActual.current = pedir;

  const cargar = useCallback(async () => {
    const peticion = ++actual.current;
    setEstado({ cargando: true, error: false });
    try {
      const result = await pedirActual.current();
      if (peticion !== actual.current) return null;
      if (!result.statusResponse) {
        setEstado({ cargando: false, error: true });
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
        } else if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return null;
      }
      setEstado({ cargando: false, error: false });
      return result;
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      if (peticion !== actual.current) return null;
      setEstado({ cargando: false, error: "conexion" });
      return null;
    }
  }, [setIsUnautorized]);

  return { ...estado, cargar };
};

/**
 * Lista de una pestaña: carga local (esqueleto y error con "Reintentar") y recarga con
 * `setReload(!reload)`, que es lo que ya usan los diálogos al guardar. `extraer` saca la lista
 * de la respuesta (por defecto `data.result`).
 */
export const useListaRemota = (pedir, extraer = (resultado) => resultado.data.result) => {
  const { cargando, error, cargar } = useCargaLocal(pedir);
  const [datos, setDatos] = useState([]);
  const [reload, setReload] = useState(false);

  useEffect(() => {
    let vigente = true;
    (async () => {
      const resultado = await cargar();
      if (vigente && resultado) setDatos(extraer(resultado));
    })();
    return () => {
      vigente = false;
    };
  }, [reload]);

  return { datos, cargando, error, reload, setReload };
};

/** "01/10/2026 – 15/10/2026" */
export const periodo = (desde, hasta) => `${formatearFecha(desde)} – ${formatearFecha(hasta)}`;

/**
 * Una escritura al servidor (guardar, borrar, pagar…): enciende el overlay global como antes,
 * resuelve el 401 y avisa del error con `useAviso`. Resuelve true si salió bien y false si no.
 * `llamada` es la función de la capa API tal cual (se llama una sola vez).
 */
export const useEscritura = () => {
  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const aviso = useAviso();

  return useCallback(
    async (llamada, mensajeError = "No se pudo completar la acción") => {
      setIsLoading(true);
      try {
        const result = await llamada();
        if (!result.statusResponse) {
          setIsLoading(false);
          if (result.error === "Unauthorized") {
            setIsUnautorized(true);
            return false;
          }
          if (result.error === ERROR_OTRA_SESION) {
            simpleMessage(result.error, "error");
            return false;
          }
          aviso.error(result.error || mensajeError);
          return false;
        }
        setIsLoading(false);
        return true;
      } catch (e) {
        setIsLoading(false);
        aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
        return false;
      }
    },
    [aviso, setIsLoading, setIsUnautorized]
  );
};

/**
 * Corta el doble envío: el ref ignora el segundo clic (o Enter) al instante, aunque el estado
 * `guardando` todavía no haya llegado a pintarse.
 */
export const useEnvio = () => {
  const enviando = useRef(false);
  const [guardando, setGuardando] = useState(false);

  const enviar = useCallback(async (accion) => {
    if (enviando.current) return undefined;
    enviando.current = true;
    setGuardando(true);
    try {
      return await accion();
    } finally {
      enviando.current = false;
      setGuardando(false);
    }
  }, []);

  return { guardando, enviar };
};

/**
 * Confirmación de una acción sobre un registro (borrar, pagar, calcular…).
 * `pedir({ titulo, mensaje, textoConfirmar, peligro, onConfirmar })` abre el diálogo; solo al
 * pulsar el botón de confirmar se llama a `onConfirmar`, una vez, con `cargando` hasta que termine.
 * Pintar `dialogo` una vez en la pantalla.
 */
export const useConfirmacion = () => {
  const [estado, setEstado] = useState({ abierto: false, config: null });
  const [procesando, setProcesando] = useState(false);

  const pedir = useCallback((config) => setEstado({ abierto: true, config }), []);
  // Se conserva la configuración al cerrar para que el texto no cambie durante la animación.
  const cerrar = useCallback(
    () => setEstado((actual) => ({ ...actual, abierto: false })),
    []
  );

  const confirmar = async () => {
    setProcesando(true);
    try {
      await estado.config?.onConfirmar?.();
    } finally {
      setProcesando(false);
      cerrar();
    }
  };

  const config = estado.config ?? {};
  const dialogo = (
    <ConfirmDialog
      abierto={estado.abierto}
      peligro={Boolean(config.peligro)}
      cargando={procesando}
      titulo={config.titulo}
      mensaje={config.mensaje}
      textoConfirmar={config.textoConfirmar}
      onConfirmar={confirmar}
      onCancelar={cerrar}
    />
  );

  return { pedir, dialogo };
};

/**
 * Diálogo de formulario: responsive, con título, botón Cerrar y las acciones abajo. Lo escrito no
 * se pierde: un clic fuera o Escape no lo cierran si hay datos escritos (`hayCambios`), ni
 * mientras guarda. Enter dentro de un campo envía el formulario (una sola vez).
 *
 * @param {object} props
 * @param {boolean} [props.abierto]
 * @param {React.ReactNode} props.titulo
 * @param {Function} props.onCerrar
 * @param {boolean} [props.hayCambios] Hay algo escrito que no se debe perder con un clic fuera.
 * @param {boolean} [props.guardando]
 * @param {boolean} [props.deshabilitado] Deshabilita el botón principal (p. ej. faltan datos por cargar).
 * @param {Function} [props.onEnviar] Se llama al pulsar el botón principal o con Enter.
 * @param {string} [props.textoEnviar] Texto del botón principal.
 * @param {string} [props.textoGuardando]
 * @param {boolean} [props.sinAcciones] Sin botones (el contenido trae los suyos).
 * @param {"sm"|"md"} [props.ancho]
 */
export const DialogoFormulario = ({
  abierto = true,
  titulo,
  onCerrar,
  hayCambios = false,
  guardando = false,
  deshabilitado = false,
  onEnviar,
  textoEnviar = "Guardar",
  textoGuardando = "Guardando…",
  sinAcciones = false,
  ancho = "sm",
  children,
}) => {
  const idTitulo = useId();
  const reducirMovimiento = useMediaQuery("(prefers-reduced-motion: reduce)");

  const cerrar = (_evento, razon) => {
    if (guardando) return;
    if ((razon === "backdropClick" || razon === "escapeKeyDown") && hayCambios) return;
    onCerrar();
  };

  const enviar = (evento) => {
    evento.preventDefault();
    if (onEnviar) onEnviar();
  };

  return (
    <Dialog
      open={Boolean(abierto)}
      onClose={cerrar}
      fullWidth
      maxWidth={ancho}
      aria-labelledby={idTitulo}
      transitionDuration={reducirMovimiento ? 0 : undefined}
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 1.5, sm: 4 },
          maxWidth: {
            xs: "calc(100% - 24px)",
            sm: ancho === "md" ? 900 : 560,
          },
          width: "100%",
        },
      }}
    >
      {/* El formulario envuelve al contenido y no es el Paper: un <form> con role="dialog" no es válido. */}
      <Box
        component={sinAcciones ? "div" : "form"}
        onSubmit={sinAcciones ? undefined : enviar}
        noValidate={sinAcciones ? undefined : true}
        sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}
      >
        <DialogTitle
          id={idTitulo}
          component="h2"
          sx={{ fontWeight: 700, color: "primary.main", pr: 7 }}
        >
          {titulo}
        </DialogTitle>
        <IconButton
          aria-label="Cerrar"
          onClick={() => cerrar()}
          disabled={guardando}
          sx={{
            position: "absolute",
            top: (theme) => theme.spacing(1.5),
            right: (theme) => theme.spacing(1.5),
            color: "text.secondary",
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>{children}</DialogContent>
        {sinAcciones ? null : (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => cerrar()} disabled={guardando}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={guardando || deshabilitado}
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {guardando ? textoGuardando : textoEnviar}
            </Button>
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
};

/** Campo de monto en córdobas: solo dígitos y un punto, con la validación junto al campo. */
export const CampoMonto = ({ onChange, error, ayuda, ...resto }) => (
  <TextField
    fullWidth
    onChange={(e) => {
      const valor = e.target.value;
      if (esMontoEscribible(valor)) onChange(valor);
    }}
    error={Boolean(error)}
    helperText={error || ayuda || " "}
    inputProps={{ inputMode: "decimal", autoComplete: "off" }}
    InputProps={{
      startAdornment: <InputAdornment position="start">C$</InputAdornment>,
    }}
    {...resto}
  />
);

/**
 * Fecha con el formato de la app (DD/MM/YYYY) y la validación junto al campo. Con id, el lector de
 * pantalla une la etiqueta, la ayuda y el error al campo.
 */
export const CampoFecha = ({ label, value, onChange, error, ayuda, disabled, required }) => {
  const id = useId();
  return (
    <DesktopDatePicker
      closeOnSelect
      label={label}
      inputFormat="DD/MM/YYYY"
      value={value}
      onChange={onChange}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          id={id}
          fullWidth
          required={required}
          error={Boolean(error) || Boolean(params.error)}
          helperText={error || ayuda || " "}
        />
      )}
    />
  );
};

// Un campo del formulario mientras llegan los datos.
const CampoEsqueleto = ({ md = 6 }) => (
  <Box sx={{ gridColumn: { xs: "span 12", md: `span ${md}` } }}>
    <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2.5, ...sinAnimacion }} />
  </Box>
);

/** Esqueleto de la ficha del empleado mientras llegan sus datos. */
export const CargandoFormularioEmpleado = ({ etiqueta = "Cargando…" }) => (
  <Box role="status" aria-live="polite">
    <Box component="span" sx={ocultoVisual}>
      {etiqueta}
    </Box>
    <Box
      aria-hidden="true"
      sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(12, minmax(0, 1fr))" }}
    >
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <CampoEsqueleto key={i} />
      ))}
    </Box>
  </Box>
);

/** Dato "etiqueta: valor" de un resumen. */
export const DatoResumen = ({ etiqueta, children }) => (
  <Box component="div" sx={{ minWidth: 0 }}>
    <Typography
      component="dt"
      variant="caption"
      sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}
    >
      {etiqueta}
    </Typography>
    <Box component="dd" sx={{ m: 0, mt: 0.5 }}>
      {children}
    </Box>
  </Box>
);
